import { join } from "node:path";
import { claudeHome, installedPluginDir, pluginManifestDir, pluginSourceDir } from "./paths.js";
import { copyTree, exists, readFile } from "./fsx.js";
import { run, which } from "./exec.js";
import { MCP_URL } from "./urls.js";

export const MCP_NAME = "carrier";
export { MCP_URL };

export interface InstallReport {
  copiedTo: string;
  marketplaceAdded: boolean;
  pluginInstalled: boolean;
  mcpAdded: boolean;
  claudeFound: boolean;
  notes: string[];
}

/**
 * Install the bundled Carrier Claude Code plugin:
 *  1. copy plugin → ~/.claude/plugins/carrier
 *  2. best-effort register marketplace + install via the `claude` CLI
 *  3. best-effort register the Carrier MCP server (HTTP + OAuth, zero-cred URL)
 * Best-effort steps never hard-fail; a manual fallback is always reported.
 *
 * Entry A: the MCP URL needs no pre-shared token. First tool use in Claude
 * opens Clerk OAuth (sign-up / sign-in). Operators never paste API keys for
 * the interactive path.
 */
export async function installPlugin(): Promise<InstallReport> {
  const marketplace = pluginSourceDir();
  const pluginRoot = pluginManifestDir();
  const dest = installedPluginDir();
  const notes: string[] = [];

  if (!(await exists(pluginRoot))) {
    throw new Error(`Bundled plugin not found at ${pluginRoot}. Reinstall @carrierllc/mcp.`);
  }

  // Fallback drop-in is the plugin itself (carrier/), not the marketplace wrapper.
  await copyTree(pluginRoot, dest);

  const claudeFound = await which("claude");
  let marketplaceAdded = false;
  let pluginInstalled = false;
  let mcpAdded = false;

  if (claudeFound) {
    const mkt = await run("claude", ["plugin", "marketplace", "add", marketplace]);
    marketplaceAdded = (mkt.ok && !/✘|failed|error/i.test(mkt.stdout + mkt.stderr)) || /already/i.test(mkt.stderr + mkt.stdout);
    if (!marketplaceAdded) notes.push(`marketplace add: ${(mkt.stderr || mkt.stdout).trim().slice(0, 200)}`);

    // `claude plugin install` exits 0 even on failure — gate on output text, not just exit code.
    const inst = await run("claude", ["plugin", "install", "carrier@carrier"]);
    const instOut = inst.stdout + inst.stderr;
    pluginInstalled =
      /✔|success(fully)? installed|already (installed|enabled)/i.test(instOut) && !/✘|failed/i.test(instOut);
    if (!pluginInstalled) notes.push(`plugin install: ${instOut.replace(/\s+/g, " ").trim().slice(0, 200)}`);

    const mcp = await run("claude", ["mcp", "add", "--transport", "http", MCP_NAME, MCP_URL]);
    mcpAdded = mcp.ok || /already|exists/i.test(mcp.stderr + mcp.stdout);
    if (!mcpAdded) notes.push(`mcp add: ${(mcp.stderr || mcp.stdout).trim().slice(0, 200)}`);
  } else {
    notes.push("`claude` CLI not on PATH — plugin copied; finish wiring with the manual commands below.");
  }

  return { copiedTo: dest, marketplaceAdded, pluginInstalled, mcpAdded, claudeFound, notes };
}

export async function pluginStatus(): Promise<{ installed: boolean; version?: string; dir: string }> {
  const dir = installedPluginDir();
  const manifest = join(dir, ".claude-plugin", "plugin.json");
  if (!(await exists(manifest))) return { installed: false, dir };
  try {
    const json = JSON.parse(await readFile(manifest, "utf8")) as { version?: string };
    return { installed: true, version: json.version, dir };
  } catch {
    return { installed: true, dir };
  }
}

export function manualCommands(): string[] {
  return [
    `claude plugin marketplace add ${pluginSourceDir()}`,
    `claude plugin install carrier@carrier`,
    `claude mcp add --transport http ${MCP_NAME} ${MCP_URL}`,
  ];
}

/** Post-install copy for non-developers (OAuth-on-first-use / zero-cred). */
export function installAuthGuidance(): string[] {
  return [
    `MCP URL (zero credentials): ${MCP_URL}`,
    "OAuth on first use: when Claude first calls Carrier, your browser opens",
    "  Clerk sign-in / sign-up (Google, GitHub, or email). No API token to paste.",
    "Stuck on auth?  claude mcp auth carrier",
    "Headless/CI only: org API key (ak_…) from https://app.carrier.llc → Settings → API Keys",
  ];
}

export { claudeHome };
